<template>
  <div id="category">
    <div v-if="categoryData" class="container">
      <script v-html="jsonld" type="application/ld+json"></script>
      <b-row class="bg-light-blue py-5 mb-5 border-radius justify-content-center">
        <h1>{{ categoryData.name }}</h1>
      </b-row>

      <b-row v-if="categoryData.content" class="justify-content-center mb-5 text-left">
        <b-col md="2" class="d-none d-md-block px-0">
          <div class="toc-wrapper sticky-top text-left">
            <div class="mb-3">Table des matières</div>
            <ul class="toc-list">
              <li v-for="(item, index) in tableOfContents" :key="index"
                :class="{ 'active': activeHeading === item.id }">
                <a :href="`#${item.id}`" @click="scrollToSection(item.id)">
                  {{ item.text }}
                </a>
              </li>
            </ul>
          </div>
        </b-col>
        <b-col md="7" class="mx-auto">
          <div v-if="categoryData.image_url" class="mb-3">
              <img class="blog-img" :alt="categoryData.slug" :src="categoryData.image_url" :width="1200" :height="630"
                style="max-width: 100%; height: auto; object-fit: contain;" loading="lazy" decoding="async" />
            </div>
          <div class="content" v-html="markdownToHtml(categoryData.content)"></div>
        </b-col>
        <b-col md="3" class="d-none d-md-block px-0 text-left">
          <div class="cta-wrapper sticky-top">
            <div class="mb-3">Découvrez Melo</div>
            <p>Optimisez votre prospection immobilière</p>
            <ul class="cta-features">
              <li>✓ 900 sources en temps réel</li>
              <li>✓ Gain de temps considérable</li>
              <li>✓ API et webhooks</li>
              <li>✓ 14 jours d'essai gratuit</li>
            </ul>
            <b-button class="w-100 mt-3 bg-blue" @click="$router.push('/register')">
              Essayer gratuitement
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-row align-v="center" class="px-3 px-md-0 pb-10">
        <b-col v-for="article in categoryArticles" :key="article.id" md="4" class="px-2 col-tablet-6">
          <router-link :to="{
            name: 'blog-post',
            params: {
              category: article.category.slug,
              slug: article.slug
            }
          }">
            <article>
              <div v-if="article.post_images && article.post_images.length > 0">
                <img :alt="article.slug" :src="article.post_images[0].image_url" class="blog-img" />
              </div>
              <h2>{{ article.title }}</h2>
              <div class="text-grey text-left">
                {{ article.date | moment("from") }}
              </div>
            </article>
          </router-link>
        </b-col>
      </b-row>
    </div>
    <Footer />
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
const md = new MarkdownIt();
import Footer from "@/components/bottombar/Footer.vue";
import articlesData from '../../articles.json';
import categoriesData from '../../categories.json';

export default {
  metaInfo() {
    return {
      title: this.categoryData.name,
      titleTemplate: "%s — Melo",
      meta: [
        { "http-equiv": "Content-Type", content: "text/html; charset=utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          name: "description",
          content: this.categoryData.summary
        },
        {
          property: "og:title",
          content: this.categoryData.name
        },
        { property: "og:site_name", content: "Melo" },
        { property: "og:type", content: "website" },
        {
          property: "og:url",
          content: `https://www.melo.io${this.$router.currentRoute.fullPath}`
        },
        {
          property: "og:image",
          content: this.categoryData.image_url || ''
        },
        {
          property: "og:description",
          content: this.categoryData.summary
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: this.categoryData.name },
        { name: "twitter:description", content: this.categoryData.summary },
        { name: "twitter:image", content: this.categoryData.image_url || '' },

        // Additional meta tags
        { name: "robots", content: "index, follow" },
        { name: "copyright", content: `© ${new Date().getFullYear()} Melo` }
      ],
      link: [
        {
          rel: "canonical",
          href: `https://www.melo.io${this.$router.currentRoute.fullPath}`
        }
      ],
    };
  },

  name: 'CategoryPage',
  components: {
    Footer
  },
  data() {
    return {
      articles: [],
      categories: [],
      tableOfContents: [],
      activeHeading: '',
    }
  },
  methods: {
    markdownToHtml(markdown) {
      if (!markdown || typeof markdown !== 'string') {
        return '';
      }
      return md.render(markdown);
    },
    generateTableOfContents() {
      const content = document.querySelector('.content');
      if (!content) return;

      const headings = content.querySelectorAll('h2');
      this.tableOfContents = Array.from(headings).map((heading, index) => {
        // Create a URL-friendly id from the heading text
        const id = heading.textContent
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        heading.id = id;
        return {
          id,
          text: heading.textContent,
        };
      });
    },
    scrollToSection(id) {
      // Prevent default anchor behavior
      event.preventDefault();

      const element = document.getElementById(id);
      if (element) {
        // Add offset for any fixed headers if needed
        const offset = 100; // Adjust this value based on your header height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Update the URL without triggering a route change
        const currentPath = this.$route.path;
        const newUrl = `${currentPath}#${id}`;
        history.pushState(null, '', newUrl);
      }
    },

    handleScroll() {
      if (!this.tableOfContents.length) return;

      const headings = this.tableOfContents.map(toc =>
        document.getElementById(toc.id)
      );

      // Find the current section
      let currentSection = null;
      for (const heading of headings) {
        if (!heading) continue;

        const rect = heading.getBoundingClientRect();
        const offset = 150; // Adjust this to match your header height + some padding

        if (rect.top <= offset) {
          currentSection = heading.id;
        } else {
          break;
        }
      }

      this.activeHeading = currentSection;
    },
  },
  computed: {
    jsonld() {
      return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "mainEntity": {
          "@type": "ItemList",
          "itemListElement": this.categoryArticles.map((article, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Article",
              "headline": article.title,
              "url": `https://www.melo.io/${article.category.slug}/${article.slug}`,
              "datePublished": article.date,
              "image": article.post_images && article.post_images.length > 0
                ? article.post_images[0].image_url
                : null,
            }
          }))
        },
        "name": this.categoryData.name,
        "description": this.categoryData.summary,
        "url": `https://www.melo.io${this.$router.currentRoute.fullPath}`,
        "publisher": {
          "@type": "Organization",
          "name": "Melo",
          "url": "https://www.melo.io"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@id": "https://www.melo.io",
                "name": "Home"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@id": `https://www.melo.io${this.$router.currentRoute.fullPath}`,
                "name": this.categoryData.name
              }
            }
          ]
        }
      }
    },

    categoryData() {
      const category = this.categories.find(category =>
        category.slug === this.$route.params.category
      );

      // Return null if either category is not found or content is null
      return category ? category : null;
    },
    categoryArticles() {
      return this.articles.filter(article =>
        article.category && article.category.slug === this.$route.params.category
      ).sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  },
  created() {
    this.articles = articlesData;
    this.categories = categoriesData;
  },
  mounted() {
    this.generateTableOfContents();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style lang="scss">
@import "@/styles/markdown.scss";
</style>

<style lang="scss" scoped>
.blog-img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: $radius;
}


article {
  margin-bottom: 2rem;
  transition: transform 0.2s ease-in-out;

  h2 {
    margin-top: 1rem;
    text-align: left;
    font-size: 18px;
    color: $body-text;
    font-weight: bold;
  }

  &:hover {
    transform: translateY(-5px);

    h2 {
      color: $blue;
    }
  }
}
</style>
